import React from 'react';
import { PackMeta, SortTh } from './StockPrimitives';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
const LowStockTab = ({ lowStock, filtered, lowSort, setLowSort, onAddToOrder, cartCatalogIds, locked = false }) => {
  const rows = filtered(lowStock);

  const sorted = [...rows].sort((a, b) => {
    let av, bv;
    switch (lowSort.col) {
      case 'product_name':   av = (a.product_name || '').toLowerCase(); bv = (b.product_name || '').toLowerCase(); break;
      case 'current_stock':  av = a.current_stock ?? -Infinity; bv = b.current_stock ?? -Infinity; break;
      case 'velocity':       av = a.velocity ?? -Infinity; bv = b.velocity ?? -Infinity; break;
      case 'reorder_level':  av = a.reorder_level ?? -Infinity; bv = b.reorder_level ?? -Infinity; break;
      case 'shortfall':      av = Math.max(0, (a.reorder_level ?? 0) - (a.current_stock ?? 0)); bv = Math.max(0, (b.reorder_level ?? 0) - (b.current_stock ?? 0)); break;
      case 'rev_at_risk':    av = a.rev_at_risk ?? -Infinity; bv = b.rev_at_risk ?? -Infinity; break;
      default:               av = 0; bv = 0;
    }
    if (av < bv) return lowSort.dir === 'asc' ? -1 : 1;
    if (av > bv) return lowSort.dir === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div className="overflow-x-auto w-full">
    <table className="w-full min-w-[600px] text-sm">
      <thead>
        <tr className="border-b border-border text-muted-foreground text-xs uppercase tracking-wide">
          <SortTh col="product_name"  label="Product"       sort={lowSort} setSort={setLowSort} />
          <SortTh col="current_stock" label="Current Stock" sort={lowSort} setSort={setLowSort} right />
          <SortTh col="velocity"      label="Units/Day"     sort={lowSort} setSort={setLowSort} right />
          <SortTh col="reorder_level" label="Reorder Level" sort={lowSort} setSort={setLowSort} right />
          <SortTh col="shortfall"     label="Shortfall"     sort={lowSort} setSort={setLowSort} right />
          <th
            onClick={() => setLowSort(s => s.col === 'rev_at_risk' ? { col: 'rev_at_risk', dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col: 'rev_at_risk', dir: 'desc' })}
            className="text-right py-3 px-4 cursor-pointer select-none whitespace-nowrap"
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 justify-end">
                    Rev at Risk
                    <span className="text-muted-foreground/60 text-[10px]">ⓘ</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-xs text-left normal-case font-normal tracking-normal">
                  <p className="font-medium mb-1">Revenue at Risk calculation:</p>
                  <p><span className="font-medium">Velocity</span> = weighted average of weekly units/day over 30 days (W1×1, W2×2, W3×3, W4×4)</p>
                  <p className="mt-1"><span className="font-medium">Rev at Risk</span> = velocity × avg sell price × (lead time + buffer days)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </th>
          <th className="py-3 px-4" />
        </tr>
      </thead>
      <tbody>
        {sorted.map(p => (
          <tr key={p.catalog_id || p.product_id} className="border-b border-border last:border-0 hover:bg-muted/30">
            <td className="py-3 px-4">
              <span className="font-medium">{p.product_name}</span>
              {p.is_out_of_stock && (
                <span className="ml-2 text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium">Out of Stock</span>
              )}
              <div className="mt-0.5"><PackMeta packSize={p.pack_size} unit={p.unit} /></div>
            </td>
            <td className="py-3 px-4 text-right text-red-600 font-semibold">{p.current_stock}</td>
            <td className="py-3 px-4 text-right">
              {p.velocity > 0
                ? <span className="font-medium">{p.velocity.toFixed(2)}</span>
                : <span className="text-muted-foreground/40 text-xs">—</span>
              }
            </td>
            <td className="py-3 px-4 text-right">{p.reorder_level}</td>
            <td className="py-3 px-4 text-right text-amber-600 font-medium">{Math.max(0, Math.round(p.reorder_level - p.current_stock))}</td>
            <td className="py-3 px-4 text-right text-red-600 font-medium">
              {p.rev_at_risk > 0
                ? `₹${p.rev_at_risk.toFixed(0)}`
                : <span className="text-muted-foreground/40 text-xs">—</span>
              }
            </td>
            <td className="py-3 px-4 text-right">
              {!p.insufficient_data && (
                cartCatalogIds?.has(p.catalog_id) ? (
                  <span className="flex items-center justify-end gap-1 text-xs border border-blue-200 text-blue-600 bg-blue-50 px-2 py-1 rounded-lg whitespace-nowrap">
                    ✓ Added
                  </span>
                ) : (
                  <button
                    onClick={() => !locked && onAddToOrder?.(p)}
                    disabled={locked}
                    className={`text-xs border px-2 py-1 rounded-lg whitespace-nowrap transition-colors ${
                      locked
                        ? 'border-border text-muted-foreground/50 cursor-not-allowed opacity-50'
                        : 'border-border text-foreground hover:bg-muted'
                    }`}
                  >
                    + Add to Order
                  </button>
                )
              )}
            </td>
          </tr>
        ))}
        {sorted.length === 0 && (
          <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">All stock levels healthy</td></tr>
        )}
      </tbody>
    </table>
    </div>
  );
};

export default LowStockTab;
